import { create } from 'zustand';
import { io, type Socket } from 'socket.io-client';
import type { GhostTrack, MapSnapshot, Meaning, Preferences, PublicActor } from '@smysly/shared';
import { api, ApiError } from './lib/api.js';
import { initializeTelegram, telegram } from './lib/telegram.js';
import { emitWorldEffect } from './game/events.js';

type Phase = 'booting' | 'intro' | 'world' | 'error';

interface GameState {
  phase: Phase;
  returning: boolean;
  snapshot: MapSnapshot | null;
  actors: PublicActor[];
  ghostTracks: GhostTrack[];
  selfPublicId: string | null;
  selectedId: string | null;
  assistantMessage: string;
  busy: boolean;
  error: string | null;
  shard: string | null;
  socket: Socket | null;
  bootstrap(): Promise<void>;
  enterWorld(): Promise<void>;
  chooseMeanings(titles: string[]): Promise<void>;
  sendMessage(message: string): Promise<void>;
  selectMeaning(id: string | null): void;
  updateMeaning(id: string, patch: Record<string, unknown>): Promise<void>;
  popMeaning(id: string): Promise<void>;
  removeShell(id: string): Promise<void>;
  splitMeaning(id: string, titles: [string, string]): Promise<void>;
  mergeMeanings(sourceId: string, targetId: string): Promise<void>;
  transferEnergy(id: string): Promise<void>;
  updatePreferences(patch: Partial<Preferences>): Promise<void>;
  clearError(): void;
  debugActor(kind: 'live' | 'ghost'): void;
  simulateReconnect(): void;
}

const FIRST_QUESTION = 'Вспомните последние несколько лет.\n\nКакие моменты вашей жизни вы действительно считаете стоящими того, чтобы их прожить?';
const RETURN_QUESTION = 'Ваша карта жива.\n\nПосмотрим, что изменилось? Какой смысл за это время наполнился, а какой стал тише?';

export const useGameStore = create<GameState>((set, get) => ({
  phase: 'booting', returning: false, snapshot: null, actors: [], ghostTracks: [], selfPublicId: null, selectedId: null,
  assistantMessage: FIRST_QUESTION, busy: false, error: null, shard: null, socket: null,

  async bootstrap() {
    initializeTelegram();
    try {
      const webApp = telegram();
      if (webApp?.initData) await api.telegramAuth(webApp.initData);
      else if (import.meta.env.DEV) await api.devAuth();
      else throw new Error('Откройте игру через Telegram-бота — так мир сможет узнать вас и сохранить вашу карту смыслов.');
      const me = await api.me();
      set({ phase: 'intro', returning: me.hasMap, selfPublicId: me.user.publicId, assistantMessage: me.hasMap ? RETURN_QUESTION : FIRST_QUESTION });
      void api.analytics('app_opened');
    } catch (error) {
      set({ phase: 'error', error: friendlyError(error) });
    }
  },

  async enterWorld() {
    set({ busy: true, error: null });
    try {
      const snapshot = await api.startSession();
      set({ phase: 'world', snapshot, busy: false, assistantMessage: snapshot.meanings.length ? RETURN_QUESTION : FIRST_QUESTION });
      connectRealtime(set, get);
      void api.analytics(snapshot.meanings.length ? 'session_resumed' : 'world_entered');
    } catch (error) {
      set({ busy: false, error: friendlyError(error) });
    }
  },

  async chooseMeanings(titles) {
    if (!titles.length || get().busy) return;
    set({ busy: true, error: null });
    try {
      const previous = get().snapshot;
      const snapshot = await api.selectMeanings(titles);
      const created = snapshot.meanings.filter((meaning) => !previous?.meanings.some((old) => old.id === meaning.id));
      set({ snapshot, busy: false });
      if (created.length) {
        soundEffectForSelection(created.length);
        void api.analytics(previous?.meanings.length ? 'meaning_created' : 'first_meaning_created');
      }
    } catch (error) {
      set({ busy: false, error: friendlyError(error) });
    }
  },

  async sendMessage(message) {
    const snapshot = get().snapshot;
    if (!snapshot || get().busy) return;
    set({ busy: true, error: null });
    if (snapshot.meanings.length === 0) void api.analytics('first_answer_sent');
    try {
      const response = await api.sendMessage(snapshot.session.id, message);
      const created = response.map.meanings.filter((meaning) => !snapshot.meanings.some((old) => old.id === meaning.id));
      set({ snapshot: response.map, assistantMessage: response.message, busy: false });
      if (created.length) void api.analytics(snapshot.meanings.length === 0 ? 'first_meaning_created' : 'meaning_created');
    } catch (error) {
      set({ busy: false, error: friendlyError(error) });
    }
  },

  selectMeaning(id) { set({ selectedId: id }); },

  async updateMeaning(id, patch) {
    try {
      set({ snapshot: await api.patchMeaning(id, patch), error: null });
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async popMeaning(id) {
    emitWorldEffect({ type: 'pop', id });
    await delay(520);
    try {
      const snapshot = await api.popMeaning(id);
      set({ snapshot, selectedId: null });
      void api.analytics('meaning_popped');
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async removeShell(id) {
    emitWorldEffect({ type: 'shell-pop', id });
    await delay(620);
    try {
      set({ snapshot: await api.removeShell(id) });
      void api.analytics('shell_removed');
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async splitMeaning(id, titles) {
    try {
      set({ snapshot: await api.splitMeaning(id, titles), selectedId: null });
      void api.analytics('meaning_split');
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async mergeMeanings(sourceId, targetId) {
    try {
      set({ snapshot: await api.mergeMeanings(sourceId, targetId), selectedId: targetId });
      void api.analytics('meaning_merged');
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async transferEnergy(id) {
    const snapshot = get().snapshot;
    const meaning = snapshot?.meanings.find((candidate) => candidate.id === id);
    if (!snapshot || !meaning || snapshot.releasedEnergy <= 0) return;
    const amount = Math.min(snapshot.releasedEnergy, 8, 100 - meaning.importance);
    if (amount <= 0) return;
    emitWorldEffect({ type: 'energy', id, amount });
    try {
      set({ snapshot: await api.patchMeaning(id, { importance: meaning.importance + amount, releasedEnergySpend: amount }) });
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  async updatePreferences(patch) {
    try {
      set({ snapshot: await api.preferences(patch) });
      if (patch.sound === false) void api.analytics('sound_disabled');
      if (patch.haptics === false) void api.analytics('haptics_disabled');
    } catch (error) { set({ error: friendlyError(error) }); }
  },

  clearError() { set({ error: null }); },

  debugActor(kind) {
    const actor = mockActor(kind, get().actors.length);
    set({ actors: [...get().actors, actor] });
    emitWorldEffect({ type: kind === 'ghost' ? 'debug-ghost' : 'debug-live' });
  },

  simulateReconnect() {
    const socket = get().socket;
    if (!socket) return;
    socket.disconnect();
    window.setTimeout(() => socket.connect(), 700);
  },
}));

function connectRealtime(set: (patch: Partial<GameState>) => void, get: () => GameState): void {
  get().socket?.disconnect();
  const socket = io({ path: '/socket.io', withCredentials: true });
  set({ socket });
  socket.on('world:snapshot', (payload: { self: PublicActor; actors: PublicActor[]; ghostTracks?: GhostTrack[]; shard: string }) => {
    set({ actors: payload.actors, ghostTracks: payload.ghostTracks ?? [], selfPublicId: payload.self.publicId, shard: payload.shard });
  });
  socket.on('world:join', (actor: PublicActor) => set({ actors: upsertActor(get().actors, actor) }));
  socket.on('world:leave', ({ publicId }: { publicId: string }) => set({ actors: get().actors.filter((actor) => actor.publicId !== publicId) }));
  socket.on('player:move', ({ publicId, x, y }: { publicId: string; x: number; y: number }) => {
    set({ actors: get().actors.map((actor) => actor.publicId === publicId ? { ...actor, x, y } : actor) });
  });
  socket.on('meaning:create_public', ({ publicId, meaningId, publicCategory, importance }: { publicId: string; meaningId: string; publicCategory: string | null; importance: number }) => {
    set({ actors: get().actors.map((actor) => actor.publicId === publicId ? { ...actor, meanings: [...actor.meanings.filter((meaning) => meaning.id !== meaningId), { id: meaningId, publicCategory, importance }] } : actor) });
  });
  socket.on('meaning:resize_public', ({ publicId, meaningId, importance }: { publicId: string; meaningId: string; importance: number }) => {
    set({ actors: get().actors.map((actor) => actor.publicId === publicId ? { ...actor, meanings: actor.meanings.map((meaning) => meaning.id === meaningId ? { ...meaning, importance } : meaning) } : actor) });
  });
  socket.on('meaning:pop_public', ({ publicId, meaningId }: { publicId: string; meaningId: string }) => {
    set({ actors: get().actors.map((actor) => actor.publicId === publicId ? { ...actor, meanings: actor.meanings.filter((meaning) => meaning.id !== meaningId) } : actor) });
  });
}

function upsertActor(actors: PublicActor[], incoming: PublicActor): PublicActor[] {
  return [...actors.filter((actor) => actor.publicId !== incoming.publicId), incoming];
}

function mockActor(kind: 'live' | 'ghost', index: number): PublicActor {
  return {
    publicId: crypto.randomUUID(), kind, x: -0.75 + (index % 5) * 0.35, y: -0.5 + (index % 3) * 0.4,
    meanings: [{ id: crypto.randomUUID(), publicCategory: kind === 'ghost' ? 'Чей-то смысл' : null, importance: 22 }],
  };
}

function friendlyError(error: unknown): string {
  if (error instanceof ApiError && error.code === 'AI_TEMPORARILY_UNAVAILABLE') return 'Проводник на мгновение затих. Ваш ответ сохранён — попробуйте ещё раз.';
  return error instanceof Error ? error.message : 'Мир на мгновение потерял связь.';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function soundEffectForSelection(count: number): void {
  for (let index = 0; index < Math.min(3, count); index += 1) {
    window.setTimeout(() => emitWorldEffect({ type: 'selection' }), index * 90);
  }
}

export function selectedMeaning(state: GameState): Meaning | null {
  return state.snapshot?.meanings.find((meaning) => meaning.id === state.selectedId) ?? null;
}
